import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { CTAButton, GradientText } from './UI';
import { Star, ShieldCheck, Users, Zap, Gift, CheckCircle2, Laptop, Clock, CreditCard, Bot } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-[90vh] flex flex-col items-center justify-center pt-16 md:pt-24 pb-24 px-4 bg-black overflow-hidden" data-id="element-114">
      {/* Linear-style Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/20 blur-[120px] rounded-full pointer-events-none" data-id="element-115" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-yellow-500/10 blur-[150px] rounded-full pointer-events-none" data-id="element-116" />

      <div className="container mx-auto max-w-6xl relative z-10" data-id="element-117">
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-118">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-gray-300 mb-8 backdrop-blur-md" data-id="element-119">
            <Zap className="w-4 h-4 text-yellow-400" data-id="element-120" />
            <span data-id="element-121">Deja de consumir. Empieza a construir.</span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-6" data-id="element-122">
            Crea y Vende tu Primer Producto Digital en 7 Días{' '}
            <GradientText data-id="element-123">Usando IA.</GradientText>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl md:text-2xl text-gray-300 mb-6 leading-relaxed max-w-3xl mx-auto font-medium" data-id="element-124">
            El sistema paso a paso para transformar tus ideas en un activo
            digital rentable que trabaje por ti 24/7.
          </motion.p>

          {/* Objections upfront */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="flex flex-wrap justify-center gap-4 md:gap-8 mb-10 text-gray-400 font-medium" data-id="element-125">
            <span className="flex items-center gap-2" data-id="element-126">
              <CheckCircle2 className="w-5 h-5 text-green-400" data-id="element-127" /> Sin
              experiencia
            </span>
            <span className="flex items-center gap-2" data-id="element-128">
              <CheckCircle2 className="w-5 h-5 text-green-400" data-id="element-129" /> Sin audiencia
            </span>
            <span className="flex items-center gap-2" data-id="element-130">
              <CheckCircle2 className="w-5 h-5 text-green-400" data-id="element-131" /> Sin saber
              programar
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="flex flex-col items-center gap-4" data-id="element-132">
            <CTAButton text="SÍ, QUIERO CREAR MI ACTIVO DIGITAL" subtext="Acceso inmediato + 3 Bonos exclusivos" variant="primary" data-id="element-133" />
          </motion.div>
        </div>

        {/* Premium Visual Composition */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.8
      }} className="relative mt-20 max-w-5xl mx-auto" data-id="element-134">
          {/* Badges */}
          <div className="absolute -top-6 md:-top-10 left-0 right-0 z-30 flex flex-wrap justify-center gap-3 md:gap-6 px-4" data-id="element-135">
            <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-gray-200 shadow-xl" data-id="element-136">
              <Clock className="w-4 h-4 text-yellow-400" data-id="element-137" /> Acceso inmediato
            </div>
            <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-gray-200 shadow-xl" data-id="element-138">
              <CreditCard className="w-4 h-4 text-green-400" data-id="element-139" /> Pago único
            </div>
            <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-gray-200 shadow-xl" data-id="element-140">
              <ShieldCheck className="w-4 h-4 text-blue-400" data-id="element-141" /> Garantía 7 días
            </div>
            <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-gray-200 shadow-xl hidden sm:flex" data-id="element-142">
              <Bot className="w-4 h-4 text-purple-400" data-id="element-143" /> Compatible con ChatGPT
            </div>
          </div>

          <div className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12" data-id="element-144">
            {/* Main Book */}
            <div className="relative z-20 w-full max-w-[300px] md:max-w-[400px]" data-id="element-145">
              <div className="absolute inset-0 bg-blue-500/20 blur-[60px] rounded-full" data-id="element-146" />
              <img src="https://cdn.magicpatterns.com/uploads/d27q3JeVdd13pGBbntuQ6W/portada_(tu_primer_activo_digital).png" alt="Tu Primer Activo Digital" className="w-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative z-10" data-id="element-147" />
            </div>

            {/* Bonuses Stack */}
            <div className="relative z-10 flex flex-row md:flex-col gap-4 md:gap-6 w-full max-w-[300px]" data-id="element-148">
              <div className="absolute inset-0 bg-yellow-500/10 blur-[60px] rounded-full" data-id="element-149" />

              <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 flex items-center gap-4 backdrop-blur-md shadow-2xl transform md:-translate-x-12 hover:translate-x-0 transition-transform" data-id="element-150">
                <img src="https://cdn.magicpatterns.com/uploads/1REYp1XjgTYR1Gn6SzPHu8/bono_1.png" alt="Bono 1: 500 Prompts Premium" loading="lazy" decoding="async" className="w-16 md:w-20 object-contain drop-shadow-md" data-id="element-151" />
                <div data-id="element-152">
                  <div className="text-xs text-yellow-400 font-bold mb-1" data-id="element-153">
                    BONO 1
                  </div>
                  <div className="text-sm md:text-base font-bold text-white leading-tight" data-id="element-154">
                    500 Prompts Premium
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 flex items-center gap-4 backdrop-blur-md shadow-2xl transform md:translate-x-8 hover:translate-x-0 transition-transform" data-id="element-155">
                <img src="https://cdn.magicpatterns.com/uploads/mvo5ohXqjEEHAYiKLHin8u/bono_2.png" alt="Bono 2: 50 Nichos Rentables" loading="lazy" decoding="async" className="w-16 md:w-20 object-contain drop-shadow-md" data-id="element-156" />
                <div data-id="element-157">
                  <div className="text-xs text-yellow-400 font-bold mb-1" data-id="element-158">
                    BONO 2
                  </div>
                  <div className="text-sm md:text-base font-bold text-white leading-tight" data-id="element-159">
                    50 Nichos Rentables
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 flex items-center gap-4 backdrop-blur-md shadow-2xl transform md:-translate-x-4 hover:translate-x-0 transition-transform" data-id="element-160">
                <img src="https://cdn.magicpatterns.com/uploads/2mcY1V47emJykrePiEj7Wm/bono_3.png" alt="Bono 3: 200 Hooks Virales" loading="lazy" decoding="async" className="w-16 md:w-20 object-contain drop-shadow-md" data-id="element-161" />
                <div data-id="element-162">
                  <div className="text-xs text-yellow-400 font-bold mb-1" data-id="element-163">
                    BONO 3
                  </div>
                  <div className="text-sm md:text-base font-bold text-white leading-tight" data-id="element-164">
                    200 Hooks Virales
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Authority Strip */}
      <div className="w-full border-y border-white/10 bg-white/5 backdrop-blur-md py-6 mt-20 relative z-20" data-id="element-165">
        <div className="container mx-auto max-w-5xl flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-70" data-id="element-166">
          <div className="flex items-center gap-2" data-id="element-167">
            <Users className="w-6 h-6" data-id="element-168" />
            <span className="font-bold text-white" data-id="element-169">+1,000 Creadores</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-170">
            <div className="flex text-yellow-400" data-id="element-171">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-current" data-id="element-172" />)}
            </div>
            <span className="font-bold text-white" data-id="element-173">4.9/5 Estrellas</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-174">
            <ShieldCheck className="w-6 h-6" data-id="element-175" />
            <span className="font-bold text-white" data-id="element-176">Método Probado</span>
          </div>
        </div>
      </div>
    </section>;
}